import React from "react";
import { useAppSelector } from "../../../hooks/hooks";
import {
  selectVehicleReport,
  selectVehicleReportStatus,
} from "../../../features/reports/reportsSlice";
import {
  CollapsableItemList,
  Container,
  ContainerLineItem,
  Layout,
  Padding,
  Text,
  Table,
  Tag,
  Icon,
  IconList,
} from "@kidslivesafe/sc-react";

import {
  VehicleReportType,
  ListingHistory,
} from "../../../features/reports/reportTypes";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";
import {
  formatNumberCommas,
  parseDate,
  generateList,
  renderArrayOfObjectsPrep,
} from "../../../utils";

const MarketListingHistory: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  let pulledReport = useAppSelector(selectVehicleReport) as VehicleReportType;
  const vehicleReportStatus = useAppSelector(selectVehicleReportStatus);

  const loading = vehicleReportStatus === "loading";

  const marketListing = renderArrayOfObjectsPrep(
    pulledReport?.listingHistory || [],
    [
      "dataSource",
      "id",
      "lastSeenAt",
      "scrapedAt",
      "vdpUrl",
      "scrapedAtDate",
      "state",
      "statusDate",
      "zip",
      "source",
    ],
    [
      {
        key: ["price"],
        valueFormat: (value, currentObject: ListingHistory) => {
          return "$" + formatNumberCommas(value);
        },
      },
      {
        key: ["miles"],
        keyFormat: () => {
          return "Mileage";
        },
        valueFormat: (value, currentObject: ListingHistory) => {
          return formatNumberCommas(value);
        },
      },
      {
        key: ["sellerType"],
        valueFormat: (value: string, currentObject: ListingHistory) => {
          return value.charAt(0).toUpperCase() + value.slice(1);
        },
      },
      {
        key: ["inventoryType"],
        valueFormat: (value: string, currentObject: ListingHistory) => {
          return value.charAt(0).toUpperCase() + value.slice(1);
        },
      },
      {
        key: ["firstSeenAtDate"],
        keyFormat: () => {
          return "First Seen Date";
        },
        valueFormat: (value: string, currentObject: ListingHistory) => {
          return parseDate(value, "month-day-year");
        },
      },
      {
        key: ["lastSeenAtDate"],
        keyFormat: () => {
          return "Last Seen Date";
        },
        valueFormat: (value: string, currentObject: ListingHistory) => {
          return parseDate(value, "month-day-year");
        },
      },
      {
        key: ["city"],
        keyFormat: () => {
          return "Location";
        },
        valueFormat: (value: string, currentObject: ListingHistory) => {
          return `${value}, ${currentObject.state}`;
        },
      },
      {
        key: ["firstSeenAt"],
        keyFormat: () => {
          return "Days on Market";
        },
        valueFormat: (value: string, currentObject: ListingHistory) => {
          if (currentObject.lastSeenAt && value) {
            let daysOnMarket =
              (Number(currentObject.lastSeenAt) - Number(value)) /
              (60 * 60 * 24);
            return Math.round(daysOnMarket).toString();
          }
        },
      },
    ],
  );

  function getPercentChange(currentPrice: number, previousPrice: number) {
    let number = Math.round(
      ((currentPrice - previousPrice) / previousPrice) * 100,
    );

    return Math.abs(number);
  }

  const upOrDown = (index: number) => {
    if (pulledReport.listingHistory) {
      // price goes up
      if (
        pulledReport.listingHistory[index] &&
        pulledReport.listingHistory[index + 1] &&
        pulledReport.listingHistory[index].price >
          pulledReport.listingHistory[index + 1].price &&
        pulledReport.listingHistory[index + 1].price !== 0 &&
        pulledReport.listingHistory[index].price !== 0
      ) {
        let percentChange = getPercentChange(
          pulledReport.listingHistory[index].price,
          pulledReport.listingHistory[index + 1].price,
        );
        return (
          <Tag
            type="complaint"
            tagText={
              <Layout type="flex" flexProps={{ alignItems: "center" }}>
                <Text size="xxs">↑ {percentChange}</Text>
                <Text>
                  <Icon name={IconList.IconPercentage}></Icon>
                </Text>
              </Layout>
            }
          />
        );
      } else if (
        // price goes down
        pulledReport.listingHistory[index] &&
        pulledReport.listingHistory[index + 1] &&
        pulledReport.listingHistory[index].price <
          pulledReport.listingHistory[index + 1].price &&
        pulledReport.listingHistory[index + 1].price !== 0 &&
        pulledReport.listingHistory[index].price !== 0
      ) {
        let percentChange = getPercentChange(
          pulledReport.listingHistory[index].price,
          pulledReport.listingHistory[index + 1].price,
        );
        return (
          <Tag
            type="quinary"
            tagText={
              <Layout type="flex" flexProps={{ alignItems: "center" }}>
                <Text size="xxs">↓ {percentChange}</Text>
                <Text>
                  <Icon name={IconList.IconPercentage}></Icon>
                </Text>
              </Layout>
            }
          />
        );
      } else if (
        // price stays the same
        pulledReport.listingHistory[index] &&
        pulledReport.listingHistory[index + 1] &&
        pulledReport.listingHistory[index + 1].price !== 0 &&
        pulledReport.listingHistory[index].price !== 0
      ) {
        return (
          <Tag
            type="tertiary"
            tagText={
              <Layout type="flex" flexProps={{ alignItems: "center" }}>
                &nbsp;
                <Text size="xxxs">
                  <Icon size={6} name={IconList.IconDash}></Icon>
                </Text>
                <Text size="xxxs">
                  <Icon name={IconList.IconPercentage}></Icon>
                </Text>
              </Layout>
            }
          />
        );
      }
    }
  };

  return (
    <Padding
      space="none"
      breakpointStyles={{
        mobile: {
          left: "md",
          right: "md",
        },
      }}
    >
      <Container
        containerId={containerId}
        containerTitle={`Auction and For Sale Listings ${
          loading
            ? ""
            : `(${
                pulledReport && pulledReport.listingHistory
                  ? pulledReport.listingHistory.length
                  : 0
              })`
        }`}
        tooltipContent={
          <Text size="sm" tag="div">
            This section highlights available listing and auction history for
            the vehicle, with indicators of price changes:{" "}
            <Tag
              type="quinary"
              tagText={
                <Layout type="flex" flexProps={{ alignItems: "center" }}>
                  <Text size="xxs">&nbsp;&nbsp;</Text>
                  <Text>
                    <Icon name={IconList.IconPercentage}></Icon>
                  </Text>
                </Layout>
              }
            />{" "}
            for drops,{" "}
            <Tag
              type="tertiary"
              tagText={
                <Layout type="flex" flexProps={{ alignItems: "center" }}>
                  &nbsp;
                  <Text size="xxxs">
                    <Icon size={6} name={IconList.IconDash}></Icon>
                  </Text>
                  <Text size="xxxs">
                    <Icon name={IconList.IconPercentage}></Icon>
                  </Text>
                </Layout>
              }
            />{" "}
            for no change, and{" "}
            <Tag
              type="complaint"
              tagText={
                <Layout type="flex" flexProps={{ alignItems: "center" }}>
                  <Text size="xxs">&nbsp;&nbsp;</Text>
                  <Text>
                    <Icon name={IconList.IconPercentage}></Icon>
                  </Text>
                </Layout>
              }
            />{" "}
            for increases. Some listings or auction events may be missing from
            this list.
          </Text>
        }
      >
        {loading ? (
          <LoadingContainerLineItem
            lineItemHeader
            lineItemSubHeader
            lineItemSubHeaderLine2
            lineItemDetails
            numberToLoad={5}
          />
        ) : pulledReport &&
          pulledReport.listingHistory &&
          pulledReport.listingHistory.length > 0 ? (
          <CollapsableItemList numberToShow={5} isOpen={printReady}>
            {pulledReport?.listingHistory &&
              pulledReport?.listingHistory.map((currentItem, index) => (
                <ContainerLineItem
                  key={index}
                  lineItemHeader={
                    <Layout
                      type="flex"
                      flexProps={{ gap: "xxs", alignItems: "flex-end" }}
                    >
                      <Text size="base">
                        {currentItem.price === 0 &&
                        currentItem.sellerType === "auction"
                          ? null
                          : `$${formatNumberCommas(currentItem.price)} `}
                        {currentItem.sellerType === "auction"
                          ? "Auction Listing"
                          : "For Sale"}{" "}
                        ({currentItem.inventoryType})
                      </Text>
                      {upOrDown(index)}
                    </Layout>
                  }
                  lineItemSubHeader={
                    <Layout
                      type="flex"
                      flexProps={{
                        direction: "row",
                        alignItems: "flex-start",
                        gap: "xxs",
                      }}
                    >
                      <Text size="xs" fontWeight="semibold">
                        {generateList(
                          [
                            {
                              value: currentItem.miles
                                ? `${formatNumberCommas(currentItem.miles)} Miles`
                                : null,
                              label: "",
                            },
                            {
                              value: generateList(
                                [
                                  {
                                    value: currentItem.firstSeenAtDate
                                      ? parseDate(
                                          currentItem.firstSeenAtDate,
                                          "utc:month-day-year",
                                        )
                                      : null,
                                    label: "",
                                  },
                                  {
                                    value: currentItem.lastSeenAtDate
                                      ? parseDate(
                                          currentItem.lastSeenAtDate,
                                          "utc:month-day-year",
                                        )
                                      : null,
                                    label: "",
                                  },
                                ],
                                " - ",
                              ),
                              label: "",
                            },
                          ],
                          " | ",
                        )}
                      </Text>
                    </Layout>
                  }
                  subContent={
                    !currentItem.state ? (
                      <Text size="xs" fontWeight="semibold">
                        Location Unknown
                      </Text>
                    ) : (
                      <Text size="xs" fontWeight="semibold">
                        {generateList(
                          [
                            {
                              value: currentItem.city
                                ? currentItem.city.replace(",", "")
                                : null,
                              label: "",
                            },
                            {
                              value: currentItem.state,
                              label: "",
                            },
                          ],
                          ", ",
                        )}
                      </Text>
                    )
                  }
                  isOpen={printReady || index === 0 ? true : false}
                >
                  {pulledReport && currentItem && (
                    <Padding left="md" right="md">
                      <Table
                        tableDataObject={marketListing[index]}
                        orderOfProperties={[
                          "Price",
                          "Mileage",
                          "Seller Type",
                          "Days on Market",
                          "Last Seen Date",
                          "First Seen Date",
                          "Seller Name",
                          "Location",
                          "Inventory Type",
                        ]}
                      />
                    </Padding>
                  )}
                </ContainerLineItem>
              ))}
          </CollapsableItemList>
        ) : (
          <Padding space="md">
            <Text size="sm" fontWeight="bold">
              No listings information available for this vehicle
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default MarketListingHistory;
